import { FEATURE } from '~/common/enums/feature.enum';

import type { TreatmentWithConfig } from './types';

/**
 * Flags hardcoded to the value Split served in Production-ORB when Split was removed
 * (EDGE-33650): unchanged for 6+ months, or never created in production. Each entry is
 * the exact treatment and parsed config Split returned, so call sites behave as before.
 * To change one of these, move it to `MIXPANEL_FLAGS` or remove the flag from code.
 */
export const STATIC_FLAGS = {
  [FEATURE.ENABLE_NEW_KYC_FORM]: { treatment: 'on' },
  [FEATURE.SHOW_COMMISSIONER_ONBOARDING_CTA]: { treatment: 'on' },
  [FEATURE.ENABLE_COMMISSIONER_POSTS]: { treatment: 'off' },
  [FEATURE.ENABLE_CONTEST_DETAILS_BANNER_AND_AVATAR]: { treatment: 'on' },
  [FEATURE.CREATE_CONTEST_ENABLE_CUSTOM_FEE_AND_ENTRANTS]: { treatment: 'on' },
  [FEATURE.SHOW_CONTEST_INVITE_CTA]: { treatment: 'on' },
  [FEATURE.SHOW_CONTEST_ITEM_THUMBNAIL]: { treatment: 'on' },
  [FEATURE.SHOW_VIPP_AND_PAPER_CHECK]: { treatment: 'on' },
  [FEATURE.ENABLE_POST_COMMENTS]: { treatment: 'off' },
  [FEATURE.JURISDICTION_ENABLE_INFO_CONTEST_DETAIL]: { treatment: 'on' },
  [FEATURE.JURISDICTION_ENABLE_INFO_LOBBY]: { treatment: 'off' },
  [FEATURE.ENABLE_REPORT_POST]: { treatment: 'off' },
  [FEATURE.ENABLE_HOME_PAGE]: { treatment: 'on' },
  [FEATURE.ENABLE_HOME_PAGE_LIGHT_THEME]: { treatment: 'on' },
  [FEATURE.KYC_PRIMARY_DEPOSIT_CTA]: { treatment: 'on' },
  [FEATURE.CREATE_CONTEST_SHOW_DESCRIPTION_FIELD]: { treatment: 'on' },
  [FEATURE.SHOW_ASIDE_STATS_OWNERS]: { treatment: 'off' },
  [FEATURE.SHOW_ASIDE_STATS_LAST_GAMES]: { treatment: 'off' },
  [FEATURE.PAYPAL_DEPOSIT_SUCCESS_DELAY]: {
    treatment: 'SHORT',
    config: {
      timeout: 2000,
    },
  },
  [FEATURE.ENABLE_ANONYMOUS_PICKS]: { treatment: 'on' },
  [FEATURE.ENABLE_COMMISSIONER_LIST_INTERCOM__CONTEST_DETAILS]: { treatment: 'off' },
  [FEATURE.ENABLE_POST_PICK_MODAL_FEATURED_CONTESTS]: { treatment: 'off' },
  [FEATURE.ENABLE_NEW_GAMESHELL_FOR_CONTEST_TYPE]: {
    treatment: 'on',
    config: {
      player_tier: ['basketball', 'golf', 'football'],
      team_survivor: ['football'],
      team_pick_em: ['football'],
      player_one_and_done: ['golf', 'basketball', 'football'],
    },
  },
  [FEATURE.ENABLE_SKIP_WELCOME_SCREEN]: { treatment: 'on' },
  [FEATURE.ENABLE_VENMO]: { treatment: 'on' },
  [FEATURE.CREATE_CONTEST_NFL_TIERS_OPTION_TAGS]: { treatment: 'on' },
  [FEATURE.CREATE_CONTEST_ALLOW_SELECT_INDIVIDUAL_GAMES]: { treatment: 'on' },
  [FEATURE.CREATE_CONTEST_ALLOW_SET_PICK_DEADLINE]: { treatment: 'on' },
  [FEATURE.CREATE_CONTEST_ALLOW_WEIGHTED_SCORING]: { treatment: 'on' },
  [FEATURE.CREATE_CONTEST_NFL_PICK_X_OPTION_TAGS]: { treatment: 'off' },
  [FEATURE.ENABLE_CONTEST_COUNTDOWN_TIMER]: { treatment: 'on' },
  [FEATURE.CREATE_CONTEST_ALLOW_BEST_PICK]: { treatment: 'on' },
  [FEATURE.ENABLE_DYNAMIC_CONTEST_LINK_PREVIEW]: { treatment: 'on' },
  [FEATURE.ENABLE_DYNAMIC_CONTEST_LINK_PREVIEW_BASE64]: { treatment: 'on' },
  [FEATURE.REFETCH_INTERVAL_FOR_TEAM_BASED_PICKSHEET]: {
    treatment: 'on',
    config: {
      interval: '10000',
    },
  },
  [FEATURE.ALLOW_LIVE_GAMESHELL_POINTS]: { treatment: 'off' },
  [FEATURE.ENABLE_CANADA_IN_KYC_AND_BILLING_ADDRESS]: { treatment: 'off' },
  [FEATURE.ENABLE_NEW_HEADER_2]: { treatment: 'on' },
  [FEATURE.ENABLE_APP_PROMPT]: { treatment: 'off' },
  [FEATURE.GAMECENTER_UNSUPPORTED_TYPES_AND_FEATURES]: {
    treatment: 'on',
    config: {
      picksheet: {},
      stats: {},
      entries: {},
      standings: {},
      availability: {},
      players: {},
      entrants: {},
    },
  },
  [FEATURE.ENABLE_SIGNUP_CONTEST_CARD]: { treatment: 'on' },
  [FEATURE.ENABLE_PROPS]: { treatment: 'on' },
  [FEATURE.ENABLE_CONTEST_INVITES_TAB]: { treatment: 'on' },
  [FEATURE.ENABLE_EDIT_CONTEST]: { treatment: 'on' },
  [FEATURE.ENABLE_EDIT_CONTEST_ADVANCED_SETTINGS]: { treatment: 'on' },
  [FEATURE.ENABLE_REFER_A_FRIEND_V2]: { treatment: 'on' },
  [FEATURE.ENABLE_SURVIVOR_AVAILABILITY_PAGE]: { treatment: 'on' },
  [FEATURE.ENABLE_QUICK_PICKS_CANCEL_ENTRY]: { treatment: 'on' },
  [FEATURE.ENABLE_INVITES_MANAGER_FOLLOWERS]: { treatment: 'on' },
  [FEATURE.ENABLE_INVITES_MANAGER_GROUPS]: { treatment: 'on' },
  [FEATURE.ENABLE_INVITES_MANAGER_RYP]: { treatment: 'on' },
  [FEATURE.ENABLE_INVITES_MANAGER_OFP]: { treatment: 'on' },
  [FEATURE.ENABLE_INVITES_MANAGER_ALL]: { treatment: 'on' },
  [FEATURE.ENABLE_AEROPAY]: { treatment: 'on' },
  [FEATURE.ENABLE_DEPOSIT_MATCH_BONUS]: { treatment: 'on' },
  [FEATURE.ENABLE_ENTRIES_ENTRY_FEE_FILTER]: { treatment: 'off' },
  [FEATURE.ENABLE_HOME_QP]: { treatment: 'on' },
  [FEATURE.ENABLE_HOME_NOTIFICATION_WIDGETS]: { treatment: 'on' },
  [FEATURE.ENABLE_EDIT_CONTEST_SIZE]: { treatment: 'on' },
  [FEATURE.ENABLE_EDIT_SCHEDULE]: { treatment: 'off' },
  [FEATURE.ENABLE_EDIT_SEGMENTS]: { treatment: 'on' },
  [FEATURE.ENABLE_INVITES_DOWNLOAD]: { treatment: 'on' },
  [FEATURE.ENABLE_INVITES_REVIEW_MODAL]: { treatment: 'on' },
  [FEATURE.ENABLE_ENTRANT_MANAGER]: { treatment: 'on' },
  [FEATURE.ENABLE_POOL_MEMBERS_DOWNLOAD]: { treatment: 'on' },
  [FEATURE.ENABLE_PLAYERS_TAB_TABLE_VIEW]: {
    treatment: 'custom',
    config: ['pga'],
  },
  [FEATURE.ENABLE_INVITES_MANAGER_DRILLDOWN]: {
    treatment: 'on',
    config: {
      enabled: ['OFP', 'RYP', 'GROUP'],
    },
  },
  [FEATURE.ENABLE_WELCOME_MODALS]: { treatment: 'off' },
  [FEATURE.COMMISSIONER_FILTER_SORT]: { treatment: 'on' },
  [FEATURE.CONFIG_LOBBY_FILTERS]: {
    treatment: 'on',
    config: [
      'team_survivor',
      'team_pick_em',
      'player_survivor',
      'player_tier',
      'player_one_and_done',
    ],
  },
  [FEATURE.ENABLE_MANAGE_CONTEST]: { treatment: 'on' },
  [FEATURE.ENABLE_INVITES_MANAGER_V3]: { treatment: 'on' },
  [FEATURE.SHOW_INVITES_MANAGER_SEARCH]: { treatment: 'on' },
  // Was a 50/50 on/off rollout in Split; rolled out to 100% when Split was removed.
  [FEATURE.ENABLE_POST_PICK_QP_PROMOTION]: { treatment: 'on' },
  [FEATURE.ENABLE_ENTRANTS_MANAGER_RENAME_ENTRY]: { treatment: 'on' },
  [FEATURE.ENABLE_GAMECENTER_RAF_CALLOUT]: { treatment: 'on' },
  [FEATURE.ENABLE_ENTRANTS_MANAGER_REVIVALS]: { treatment: 'on' },
  [FEATURE.ENABLE_ENTRANTS_MANAGER_ACTIVITY]: { treatment: 'on' },
  [FEATURE.ENABLE_MANAGE_ENTRIES_PICKS]: { treatment: 'on' },
  [FEATURE.ENABLE_UNIFIED_CAC_FLOW]: { treatment: 'on' },
  [FEATURE.ENABLE_PROVE_KYC_WEB]: { treatment: 'on' },
  [FEATURE.ENABLE_PROMO_CODES]: { treatment: 'on' },
  // Never created in Production-ORB: Split returned 'control', which every call site reads as off.
  [FEATURE.ENABLE_DFS_GAME_UNITS]: { treatment: 'off' },
  [FEATURE.SHOW_PUBLIC_PRIVATE_POSTS]: { treatment: 'off' },
  [FEATURE.ENABLE_SALARY_BASED_CONTESTS]: { treatment: 'off' },
  [FEATURE.ENABLE_CHAT]: { treatment: 'off' },
  [FEATURE.ENABLE_DYNAMIC_CONTEST_LINK_PREVIEW_DEADLINE]: { treatment: 'off' },
  [FEATURE.CREATE_CONTEST_ENABLE_CHAT]: { treatment: 'off' },
  [FEATURE.ENABLE_DFS_MULTI_SLATE]: { treatment: 'off' },
  [FEATURE.ENABLE_POPULAR_CHANNELS_ON_HOME]: { treatment: 'off' },
  [FEATURE.ENABLE_PLAY_WITH_PEOPLE_YOU_FOLLOW]: { treatment: 'off' },
  [FEATURE.ENABLE_SLIP_DEBUG_BLOCK]: { treatment: 'off' },
  [FEATURE.ENABLE_PRESELECTED_PROPS]: { treatment: 'off' },
} as const satisfies Partial<Record<FEATURE, TreatmentWithConfig>>;
